import { FormEvent } from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"

import type { PageProps } from "@/types/inertia"

export default function AdminUsersNew() {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({ name: "", email: "", admin: false })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/admin/users")
  }

  return (
    <>
      <Head title="Invite user">
        <meta name="description" content="Invite a teammate — they'll get an email to set their password." />
        <meta property="og:title" content="Invite user" />
        <meta property="og:description" content="Invite a teammate — they'll get an email to set their password." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Invite user</h1>
              <p className="mt-1">
                They&apos;ll receive an email with a link to set their password.
                The link is good for 7 days.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                href="/admin/users"
                className="text-sm text-ink-muted no-underline hover:text-ink-display"
              >
                Back to users
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-8 max-w-md">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name">Name</label>
              <Input
                id="name"
                type="text"
                autoComplete="off"
                required
                aria-invalid={!!errors.name}
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
              />
              {errors.name && (
                <p className="text-xs text-danger-display">{errors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                autoComplete="off"
                required
                aria-invalid={!!errors.email}
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
              />
              {errors.email && (
                <p className="text-xs text-danger-display">{errors.email}</p>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm font-normal text-ink-body">
              <Checkbox
                checked={form.data.admin}
                onChange={(e) => form.setData("admin", e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-ink-display">Administrator</span>
                <span className="block text-xs text-ink-muted">
                  Can invite and manage users.
                </span>
              </span>
            </label>
            <Button type="submit" disabled={form.processing}>
              Send invite
            </Button>
          </form>
        </section>
      </AdminShell>
    </>
  )
}
